import React from 'react';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand';

import type { LensCatalogueDatasetSummary } from '~/types/leLens';

export type DatasetAskContext = Pick<LensCatalogueDatasetSummary, 'name' | 'purpose' | 'goodFor'> &
  Partial<Pick<LensCatalogueDatasetSummary, 'measureLabels' | 'refresh' | 'version'>>;

interface DatasetAskIntroProps {
  dataset: DatasetAskContext;
  composerSlot?: React.ReactNode;
  onAsk: (question: string) => void;
  onBack?: () => void;
}

export default function DatasetAskIntro({ composerSlot, dataset, onAsk, onBack }: DatasetAskIntroProps) {
  return (
    <Box
      sx={{
        maxWidth: 920,
        mx: 'auto',
        pb: 2,
        pt: { xs: 1, md: 2 },
        width: '100%',
      }}
    >
      {onBack ? (
        <Button
          onClick={onBack}
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ color: 'text.secondary', mb: 1.5, px: 0.5 }}
        >
          View {dataset.name} dataset details
        </Button>
      ) : null}

      <Box>
        <Stack alignItems="flex-start" spacing={1}>
          <Typography sx={{ color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>
            GOVERNED DATASET
          </Typography>
          <Typography
            component="h1"
            sx={{ fontFamily: FONT_STACK_SUISSE_WORKS, fontSize: { xs: 30, md: 38 }, lineHeight: 1.15 }}
          >
            Ask about {dataset.name}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 15, lineHeight: 1.5, maxWidth: 760 }}>
            {dataset.purpose}
          </Typography>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2.5, pt: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
            This conversation starts with the {dataset.name} dataset.
          </Typography>
          {dataset.measureLabels?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
              {Array.from(new Set(dataset.measureLabels)).slice(0, 8).map((label) => (
                <Chip key={label} label={label} size="small" sx={{ maxWidth: '100%' }} variant="outlined" />
              ))}
            </Stack>
          ) : null}
          {dataset.version || dataset.refresh ? (
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              {[
                dataset.version ? `Dataset version ${dataset.version}` : null,
                dataset.refresh ? `Refresh schedule: ${dataset.refresh}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, mb: 0.5 }}>
            What do you want to know?
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13, mb: composerSlot ? 1.5 : 0 }}>
            Write your question{composerSlot ? ' below' : ' in the composer below'}. Include a date range or comparison
            if it matters.
          </Typography>
          {composerSlot}
        </Box>

        {dataset.goodFor.length ? (
          <Box sx={{ mt: 3 }}>
            <Typography component="h2" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', mb: 0.5 }}>
              SUGGESTED QUESTIONS
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1.25 }}>
              Choose a question to ask it using this dataset.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { md: 'repeat(2, minmax(0, 1fr))', xs: '1fr' },
              }}
            >
              {dataset.goodFor.slice(0, 4).map((question) => (
                <Button
                  endIcon={<ArrowForwardRoundedIcon />}
                  key={question}
                  onClick={() => onAsk(question)}
                  sx={{
                    backgroundColor: 'action.hover',
                    borderColor: 'divider',
                    color: 'text.primary',
                    justifyContent: 'space-between',
                    minHeight: 54,
                    px: 2,
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'background.paper',
                      borderColor: 'primary.main',
                    },
                  }}
                  variant="outlined"
                >
                  {question}
                </Button>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
